import { createContext, useContext, useLayoutEffect, type ReactNode } from "react";

/**
 * A node a host (a module layout) wants drawn directly under the page title. `ModuleSubNav`
 * is a dropdown below 640px that goes under the title (SURFACES.md § Responsive), but the
 * title belongs to a page component the host wraps rather than owns. The host offers the node
 * here; the component that draws the title (`PageLayout`, or a `PageHeader` inside a
 * `headerless` layout) claims it and draws it after the title. If nothing claims it, the host
 * draws it at the top instead, so a page with no such component doesn't lose the navigation.
 */
export interface PageSubNavSlot {
  node: ReactNode;
  /** Registers a claim; the returned function releases it. */
  claim: () => () => void;
}

export const PageSubNavSlotContext = createContext<PageSubNavSlot | null>(null);

/**
 * The node to draw under this component's title, or null. Pass `enabled: false` when this
 * component doesn't draw a title (a `headerless` layout). Only a component that draws the
 * returned node may call this, since calling it claims the slot.
 */
export function useClaimPageSubNavSlot(enabled = true): ReactNode {
  const slot = useContext(PageSubNavSlotContext);
  const claim = slot?.claim;
  useLayoutEffect(() => (enabled && claim ? claim() : undefined), [enabled, claim]);
  return enabled && slot ? slot.node : null;
}
